import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/PhoneInput';
import SectionHelp from '@/components/help/SectionHelp';
import VerticalExampleBox from '@/components/help/VerticalExampleBox';
import EmptyStateGuide from '@/components/help/EmptyStateGuide';
import FieldHelp from '@/components/help/FieldHelp';
import SensitiveActionHelp from '@/components/help/SensitiveActionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import helpContent from '@/help/helpContent';
import sensitiveActions from '@/help/sensitiveActions';
import { UserCircle, Plus, ChevronRight, Archive, Shield, AtSign, AlertTriangle, GitMerge } from 'lucide-react';

const STATUS_COLORS = { active: 'bg-emerald-50 text-emerald-700', archived: 'bg-gray-100 text-gray-400' };
const CONSENT_COLORS = { unknown: 'bg-gray-100 text-gray-500', opted_in: 'bg-emerald-50 text-emerald-700', opted_out: 'bg-red-50 text-red-600' };
const CONSENT_LABELS = { unknown: 'Sin respuesta', opted_in: 'Aceptó recibir mensajes', opted_out: 'Pidió no recibir mensajes' };
const CHANNEL_LABELS = { email: 'Correo', phone: 'Teléfono', whatsapp: 'WhatsApp', sms: 'SMS', instagram: 'Instagram', facebook: 'Facebook', webchat: 'Chat web', all: 'Todos los canales' };
// Solo ofrecemos los canales que hoy funcionan de verdad; los '_future' del backend aún no operan.
const NEW_IDENTITY_CHANNELS = [
  { value: 'email', label: 'Correo electrónico' },
  { value: 'phone', label: 'Teléfono' },
  { value: 'whatsapp', label: 'WhatsApp' },
];
const EMPTY_IDENTITY = { channel: 'email', value: '' };
const channelLabel = (ch) => CHANNEL_LABELS[String(ch || '').replace('_future', '')] || ch || '—';
// Misma normalización que usa el backend, para no mandar un dato que ya está guardado.
const normalizeIdentityValue = (channel, v) => channel === 'email'
  ? String(v || '').trim().toLowerCase()
  : String(v || '').replace(/[^\d+]/g, '');

export default function ContactManager() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('list');
  const [form, setForm] = useState({ first_name: '', last_name: '', primary_email: '', primary_phone: '', contact_type: 'prospect', preferred_channel: '', tags: [] });
  const [detail, setDetail] = useState(null);
  const [consents, setConsents] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [identities, setIdentities] = useState([]);
  const [identitiesError, setIdentitiesError] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [dupesError, setDupesError] = useState(false);
  const [newIdentity, setNewIdentity] = useState(EMPTY_IDENTITY);
  const [savingIdentity, setSavingIdentity] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/tenant/contacts?page_size=50'); setContacts(data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadDetail = async (id) => {
    setDetailLoading(true); setIdentitiesError(false); setDupesError(false);
    try {
      const [c, cs, ids, dup] = await Promise.all([
        api.get(`/tenant/contacts/${id}`),
        api.get(`/tenant/contacts/${id}/consents`),
        // Datos secundarios: si fallan, la ficha igual se abre y avisamos dentro de su tarjeta.
        api.get(`/tenant/contacts/${id}/identities`).catch(() => { setIdentitiesError(true); return { data: [] }; }),
        api.get(`/tenant/contacts/${id}/duplicate-candidates`).catch(() => { setDupesError(true); return { data: { candidates: [] } }; }),
      ]);
      setDetail(c.data);
      setConsents(cs.data || []);
      setIdentities(ids.data || []);
      setDupes(dup.data?.candidates || []);
      setNewIdentity(EMPTY_IDENTITY);
      setTab('detail');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No pudimos abrir la ficha del contacto');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const { data } = await api.post('/tenant/contacts', form);
      toast.success('Contacto creado');
      const warnings = data?.duplicate_warnings || [];
      if (warnings.length) {
        const names = warnings.map(w => w.display_name || 'contacto sin nombre').join(', ');
        toast.warning(`Ojo: ya tenías ${warnings.length === 1 ? 'un contacto parecido' : `${warnings.length} contactos parecidos`} (${names}). Ábrelo desde la ficha para comparar y evitar escribirle dos veces a la misma persona.`);
      }
      setTab('list'); load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleAddIdentity = async () => {
    if (!detail) return;
    const value = String(newIdentity.value || '').trim();
    if (!value) { toast.error('Escribe el correo o el número antes de agregarlo.'); return; }
    if (newIdentity.channel === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error('Escribe un correo completo, por ejemplo nombre@empresa.com.'); return;
    }
    if (newIdentity.channel !== 'email' && value.replace(/\D/g, '').length < 8) {
      toast.error('Escribe el número completo, con clave de país y sin espacios de más.'); return;
    }
    const norm = normalizeIdentityValue(newIdentity.channel, value);
    const already = identities.some(i => i.channel === newIdentity.channel
      && normalizeIdentityValue(newIdentity.channel, i.normalized_value || i.identity_value) === norm);
    if (already) { toast.error('Ese dato ya está guardado en este canal.'); return; }
    setSavingIdentity(true);
    try {
      await api.post(`/tenant/contacts/${detail.id}/identities`, { channel: newIdentity.channel, identity_value: value });
      toast.success('Dato de contacto agregado');
      await loadDetail(detail.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No pudimos guardar el dato de contacto');
    } finally {
      setSavingIdentity(false);
    }
  };

  const handleConsent = async (channel, status) => {
    try { await api.put(`/tenant/contacts/${detail.id}/consents/${channel}`, { consent_status: status }); toast.success('Consentimiento actualizado'); loadDetail(detail.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleArchive = async (id) => {
    try { await api.patch(`/tenant/contacts/${id}/archive`); toast.success('Archivado'); setTab('list'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Deuda #5 — fusionar un contacto repetido EN el que está abierto (sobreviviente).
  // Todo lo del repetido (leads, correos, teléfonos, permisos) se mueve aquí y el
  // repetido queda archivado apuntando a este. No se borra nada.
  const handleMerge = async (duplicateId) => {
    if (!detail) return;
    try {
      const { data } = await api.post(`/tenant/contacts/${detail.id}/merge`, { duplicate_id: duplicateId });
      const n = Object.keys(data?.moved || {}).length;
      toast.success(n ? `Contactos unidos: se movió su historial a esta ficha.` : 'Contactos unidos.');
      loadDetail(detail.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo unir el contacto');
    }
  };

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-4" data-testid="contact-manager-page">
      <SectionHelp module="contacts" title={helpContent.contacts?.title || 'Contactos'} description={helpContent.contacts?.description} whatItDoesNot={helpContent.contacts?.whatItDoesNot} />
      <VerticalExampleBox module="contacts" />

      {detailLoading && (
        <div className="flex items-center gap-2 text-xs text-[#64748B]" data-testid="contact-detail-loading">
          <span className="w-3.5 h-3.5 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" /> Abriendo la ficha del contacto…
        </div>
      )}

      {tab === 'list' && (<>
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#64748B]">{contacts.length} contacto{contacts.length !== 1 ? 's' : ''}</p>
          <Button size="sm" onClick={() => { setForm({ first_name: '', last_name: '', primary_email: '', primary_phone: '', contact_type: 'prospect', preferred_channel: '', tags: [] }); setTab('form'); }} data-testid="contact-create-btn"><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>
        </div>
        {contacts.length === 0 ? <EmptyStateGuide module="contacts" message="Sin contactos. Agrega tu primer contacto para construir audiencias." ctaLabel="Crear contacto" onCta={() => setTab('form')} /> : (
          <div className="space-y-2" data-testid="contact-list">
            {contacts.map(c => (
              <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-3 cursor-pointer hover:border-[#94A3B8]" onClick={() => loadDetail(c.id)} data-testid={`contact-${c.id}`}>
                <UserCircle className="w-8 h-8 text-[#94A3B8]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0F172A] truncate">{c.display_name || `${c.first_name} ${c.last_name}`}</p>
                  <p className="text-xs text-[#94A3B8]">{c.primary_email || c.primary_phone || '—'}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLORS[c.lifecycle_status] || 'bg-gray-100'}`}>{c.lifecycle_status}</span>
                <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
              </div>
            ))}
          </div>
        )}
      </>)}

      {tab === 'form' && (
        <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="contact-form">
          <h3 className="text-sm font-bold">Nuevo contacto</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldHelp label="Nombre" help="Nombre de pila del contacto." htmlFor="contact-first-name">
              <Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} data-testid="contact-first-name" id="contact-first-name" />
            </FieldHelp>
            <FieldHelp label="Apellido" help="Apellido del contacto." htmlFor="contact-last-name">
              <Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} data-testid="contact-last-name" id="contact-last-name" />
            </FieldHelp>
            <FieldHelp label={helpContent.contacts.fields.primary_email.label} help={helpContent.contacts.fields.primary_email.help} htmlFor="contact-email">
              <Input type="email" value={form.primary_email} onChange={e => setForm({...form, primary_email: e.target.value})} data-testid="contact-email" id="contact-email" />
            </FieldHelp>
            <FieldHelp label={helpContent.contacts.fields.primary_phone.label} help={helpContent.contacts.fields.primary_phone.help} htmlFor="contact-phone">
              <PhoneInput value={form.primary_phone} onChange={e => setForm({...form, primary_phone: e.target.value})} data-testid="contact-phone" id="contact-phone" />
            </FieldHelp>
            <FieldHelp label={helpContent.contacts.fields.contact_type.label} help={helpContent.contacts.fields.contact_type.help} htmlFor="contact-type">
              <select className="w-full border rounded px-2 py-1.5 text-xs" value={form.contact_type} onChange={e => setForm({...form, contact_type: e.target.value})} data-testid="contact-type" id="contact-type">
                <option value="prospect">Prospecto</option><option value="customer">Cliente</option><option value="lead">Lead</option>
              </select>
            </FieldHelp>
            <FieldHelp label={helpContent.contacts.fields.preferred_channel.label} help={helpContent.contacts.fields.preferred_channel.help} htmlFor="contact-channel">
              <select className="w-full border rounded px-2 py-1.5 text-xs" value={form.preferred_channel} onChange={e => setForm({...form, preferred_channel: e.target.value})} data-testid="contact-channel" id="contact-channel">
                <option value="">Sin preferencia</option><option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="phone">Teléfono</option>
              </select>
            </FieldHelp>
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setTab('list')}>Cancelar</Button>
            <Button size="sm" onClick={handleSave} data-testid="contact-save-btn">Crear</Button>
          </div>
        </div>
      )}

      {tab === 'detail' && detail && (
        <div className="space-y-4" data-testid="contact-detail">
          <div className="flex items-center gap-2">
            <button onClick={() => { setTab('list'); setDetail(null); }} className="text-xs text-[#64748B]">← Volver</button>
            <h3 className="text-sm font-bold flex-1">{detail.display_name}</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLORS[detail.lifecycle_status]}`}>{detail.lifecycle_status}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-white border rounded-lg p-2"><span className="text-[#94A3B8]">Email:</span> {detail.primary_email || '—'}</div>
            <div className="bg-white border rounded-lg p-2"><span className="text-[#94A3B8]">Tel:</span> {detail.primary_phone || '—'}</div>
            <div className="bg-white border rounded-lg p-2"><span className="text-[#94A3B8]">Tipo:</span> {detail.contact_type}</div>
            <div className="bg-white border rounded-lg p-2"><span className="text-[#94A3B8]">Canal pref:</span> {detail.preferred_channel || '—'}</div>
          </div>
          {(dupes.length > 0 || dupesError) && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="contact-duplicate-warnings">
              <h4 className="text-xs font-bold text-amber-900 mb-1 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Posibles contactos repetidos
                <HelpTooltip text="Buscamos otros contactos activos con el mismo correo o con un teléfono muy parecido. No borramos ni juntamos nada de forma automática: solo te avisamos para que revises y decidas con cuál te quedas." />
              </h4>
              {dupesError ? (
                <p className="text-[11px] text-amber-800">No pudimos revisar si hay contactos repetidos en este momento. Vuelve a abrir la ficha para intentarlo de nuevo.</p>
              ) : (<>
                <p className="text-[11px] text-amber-800 mb-2">Esta persona podría estar cargada más de una vez. Ábrela para compararla, o únela a ESTA ficha (esta es la que se queda).</p>
                <div className="space-y-1.5">
                  {dupes.map((d, i) => (
                    <div
                      key={`${d.id}-${i}`}
                      className="w-full flex items-center gap-2 text-xs bg-white border border-amber-200 rounded-lg px-2 py-1.5"
                      data-testid={`contact-duplicate-${d.id}`}
                    >
                      <button type="button" onClick={() => loadDetail(d.id)}
                        className="flex-1 min-w-0 flex items-center gap-2 text-left hover:opacity-70" title="Abrir para comparar">
                        <UserCircle className="w-4 h-4 text-amber-500 shrink-0" />
                        <span className="flex-1 min-w-0 truncate font-medium text-[#0F172A]">{d.display_name || 'Contacto sin nombre'}</span>
                        <span className="text-[#64748B] truncate">{d.primary_phone || d.primary_email || '—'}</span>
                        {d.leads > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 shrink-0">{d.leads} {d.leads === 1 ? 'lead' : 'leads'}</span>}
                        <ChevronRight className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                      </button>
                      {detail.lifecycle_status !== 'archived' && (
                        <SensitiveActionHelp action={sensitiveActions.contact_merge} onConfirm={() => handleMerge(d.id)}>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] shrink-0" data-testid={`contact-merge-${d.id}`}>
                            <GitMerge className="w-3 h-3 mr-1" /> Unir aquí
                          </Button>
                        </SensitiveActionHelp>
                      )}
                    </div>
                  ))}
                </div>
              </>)}
            </div>
          )}

          <div className="bg-white border rounded-xl p-4" data-testid="contact-identities-card">
            <h4 className="text-xs font-bold mb-1 flex items-center gap-1">
              <AtSign className="w-3.5 h-3.5" /> Correos y teléfonos de este contacto
              <HelpTooltip text="Aquí ves todas las formas de localizar a esta persona: sus correos, sus teléfonos y sus números de WhatsApp. Puedes guardar más de uno. Al agregar un canal nuevo aparece también abajo, en el permiso para enviarle mensajes." />
            </h4>
            <p className="text-[11px] text-[#94A3B8] mb-2">Se usan para enviarle campañas y para reconocerla cuando escribe por cualquier canal.</p>
            {identitiesError && <p className="text-[11px] text-red-600 mb-2">No pudimos cargar sus correos y teléfonos. Vuelve a abrir la ficha para intentarlo de nuevo.</p>}
            {!identitiesError && identities.length === 0 && (
              <p className="text-[11px] text-[#94A3B8] mb-2" data-testid="contact-identities-empty">Sin correos ni teléfonos guardados todavía. Agrega el primero aquí abajo.</p>
            )}
            {identities.length > 0 && (
              <div className="space-y-1.5 mb-3" data-testid="contact-identities-list">
                {identities.map(i => (
                  <div key={i.id} className="flex items-center gap-2 text-xs" data-testid={`contact-identity-${i.id}`}>
                    <span className="w-24 text-[#64748B] shrink-0">{channelLabel(i.channel)}</span>
                    <span className="flex-1 min-w-0 truncate text-[#0F172A]">{i.identity_value}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${i.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                      {i.status === 'active' ? 'En uso' : 'Sin uso'}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {detail.lifecycle_status !== 'archived' && (
              <div className="border-t pt-3 flex flex-col md:flex-row gap-2 md:items-end">
                <div className="md:w-40">
                  <label htmlFor="contact-identity-channel" className="text-[11px] text-[#64748B] font-medium block mb-1">Tipo de dato</label>
                  <select
                    id="contact-identity-channel"
                    className="w-full border rounded px-2 py-1.5 text-xs"
                    value={newIdentity.channel}
                    onChange={e => setNewIdentity({ channel: e.target.value, value: '' })}
                    data-testid="contact-identity-channel"
                  >
                    {NEW_IDENTITY_CHANNELS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="flex-1">
                  <label htmlFor="contact-identity-value" className="text-[11px] text-[#64748B] font-medium block mb-1">
                    {newIdentity.channel === 'email' ? 'Correo electrónico' : 'Número con clave de país'}
                  </label>
                  {newIdentity.channel === 'email' ? (
                    <Input
                      id="contact-identity-value"
                      type="email"
                      placeholder="nombre@empresa.com"
                      value={newIdentity.value}
                      onChange={e => setNewIdentity({ ...newIdentity, value: e.target.value })}
                      data-testid="contact-identity-value"
                    />
                  ) : (
                    <PhoneInput
                      id="contact-identity-value"
                      value={newIdentity.value}
                      onChange={e => setNewIdentity({ ...newIdentity, value: e.target.value })}
                      data-testid="contact-identity-value"
                    />
                  )}
                </div>
                <Button size="sm" onClick={handleAddIdentity} disabled={savingIdentity} data-testid="contact-identity-add-btn">
                  <Plus className="w-4 h-4 mr-1" /> {savingIdentity ? 'Guardando…' : 'Agregar'}
                </Button>
              </div>
            )}
          </div>

          <div className="bg-white border rounded-xl p-4">
            <h4 className="text-xs font-bold mb-2 flex items-center gap-1">
              <Shield className="w-3.5 h-3.5" /> Permiso para enviarle mensajes
              <HelpTooltip text="Por cada canal guardas si la persona aceptó, rechazó o todavía no te dijo si quiere recibir mensajes tuyos. Las campañas respetan este permiso." />
            </h4>
            {consents.length === 0 ? (
              <p className="text-[11px] text-[#94A3B8]">Sin permisos registrados todavía. Agrega un correo o teléfono arriba y aparecerá aquí.</p>
            ) : (
              <div className="space-y-1.5">
                {consents.map(c => (
                  <div key={c.id} className="flex items-center gap-2 text-xs">
                    <span className="w-24 text-[#64748B] shrink-0">{channelLabel(c.channel)}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${CONSENT_COLORS[c.consent_status]}`}>{CONSENT_LABELS[c.consent_status] || c.consent_status}</span>
                    <div className="flex gap-1 ml-auto">
                      {c.consent_status !== 'opted_in' && <button type="button" onClick={() => handleConsent(c.channel, 'opted_in')} className="text-[10px] text-emerald-600 hover:underline" data-testid={`contact-consent-in-${c.channel}`}>Sí acepta</button>}
                      {c.consent_status !== 'opted_out' && <button type="button" onClick={() => handleConsent(c.channel, 'opted_out')} className="text-[10px] text-red-600 hover:underline" data-testid={`contact-consent-out-${c.channel}`}>No acepta</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {detail.lifecycle_status !== 'archived' && (
            <SensitiveActionHelp action={sensitiveActions.contact_archive} onConfirm={() => handleArchive(detail.id)}>
              <Button size="sm" variant="outline" data-testid="contact-archive-btn"><Archive className="w-3.5 h-3.5 mr-1" /> Archivar</Button>
            </SensitiveActionHelp>
          )}
        </div>
      )}
    </div>
  );
}
